/**
 * Resolve the payment step's single checkout button.
 *
 * Extracted from `PaymentDetailsStep` for the same reason as `bookingQuote`: the
 * button decides whether a card gets saved and whether a booking gets created, and
 * as a pure function every state it can be in is testable directly.
 *
 * One button, two audiences:
 * - **Guest** — "Continue" submits the card form; saving the card advances to
 *   account setup, where the booking is actually created.
 * - **Pay at the club** — when the club collects what is owed at the counter, no card
 *   is asked for: a guest's "Continue" advances without saving one, and a member's
 *   "Confirm Booking" books straight away.
 * - **Nothing to charge a card** — a member owes nothing, or their account credit covers
 *   it (credits X6): "Confirm Booking" books straight away with no card.
 * - **Member** — always "Confirm Booking". With a saved method selected it books;
 *   with the new-card form showing it submits the form, and the page books with the
 *   new card as soon as it is saved. The label never changes meaning between those
 *   two, which is the point — a button that turns from "Continue" into "Complete
 *   Booking" under the cursor books on a double-click.
 *
 * @module pages/booking/steps/checkoutCta
 */

export interface CheckoutCtaInputs {
  isAuthenticated: boolean;
  /** Member's saved-methods query has not answered yet. */
  isLoadingPaymentMethods: boolean;
  /** A saved (chargeable) method is selected. */
  hasSelectedMethod: boolean;
  /** The new-card form is rendered instead of the saved-methods list. */
  showingNewPaymentForm: boolean;
  /**
   * A submit of the card form would actually go through (reported by the form's
   * `onReadyChange`). False whenever the form is not mounted.
   */
  cardReady: boolean;
  /** `useWaiverSubmission().satisfied` — vacuously true when nothing is owed. */
  waiverSatisfied: boolean;
  /** The member's price is not known yet (`resolvePaymentRequirement === 'unknown'`). */
  isPriceUnknown: boolean;
  hasOverage: boolean;
  /** The club collects what is owed at the counter (`resolvePaymentRequirement === 'at-club'`). */
  isPayAtClub: boolean;
  /**
   * A signed-in member owes no card anything — nothing is due, or account credit covers it
   * (`resolvePaymentRequirement` is `'not-required'` or `'credit-covered'`).
   */
  isNoCardNeeded: boolean;
  /** A card save, waiver submission, or booking creation is in flight. */
  busy: boolean;
}

export type CheckoutCtaAction =
  /** Submit the card form (native `form=` submit). */
  | 'submit-card'
  /** Create the booking with the selected saved method. */
  | 'confirm-saved'
  /** Create the booking with no card — pay at the club, nothing owed, or credit covers it. Members. */
  | 'confirm-no-card'
  /** Advance to account setup with no card — the club collects at the counter. Guests. */
  | 'continue-no-card';

export interface CheckoutCta {
  testId: 'checkout-save-payment-method-btn' | 'confirm-booking-overage-btn';
  /** `null` when there is nothing the button could act on — it renders disabled. */
  action: CheckoutCtaAction | null;
  disabled: boolean;
  label: string;
}

export function resolveCheckoutCta(inputs: CheckoutCtaInputs): CheckoutCta {
  const { busy, waiverSatisfied, cardReady } = inputs;

  if (inputs.isPayAtClub) {
    return inputs.isAuthenticated
      ? {
          testId: 'confirm-booking-overage-btn',
          action: 'confirm-no-card',
          disabled: busy || !waiverSatisfied || inputs.isPriceUnknown,
          label: busy ? 'Creating Booking…' : 'Confirm Booking',
        }
      : {
          testId: 'checkout-save-payment-method-btn',
          action: 'continue-no-card',
          disabled: busy || !waiverSatisfied,
          label: busy ? 'Processing…' : 'Continue',
        };
  }

  if (inputs.isAuthenticated && inputs.isNoCardNeeded) {
    return {
      testId: 'confirm-booking-overage-btn',
      action: 'confirm-no-card',
      disabled: busy || !waiverSatisfied || inputs.isPriceUnknown,
      label: busy ? 'Creating Booking…' : 'Confirm Booking',
    };
  }

  if (!inputs.isAuthenticated) {
    // No price gate here: a guest is not charged on this step, and the account
    // step they advance to already refuses to book against an unknown price.
    return {
      testId: 'checkout-save-payment-method-btn',
      action: 'submit-card',
      disabled: busy || !cardReady || !waiverSatisfied,
      label: busy ? 'Processing…' : 'Continue',
    };
  }

  // A selection outranks a visible form: once the chain selects a just-saved
  // card, the button must book with it even if the spent form is still mounted.
  const action: CheckoutCtaAction | null = inputs.isLoadingPaymentMethods
    ? null
    : inputs.hasSelectedMethod
      ? 'confirm-saved'
      : inputs.showingNewPaymentForm
        ? 'submit-card'
        : null; // saved methods exist but none is chargeable (bank-only rows)

  const actionable = action === 'confirm-saved' || (action === 'submit-card' && cardReady);

  return {
    testId: 'confirm-booking-overage-btn',
    action,
    disabled: busy || !waiverSatisfied || inputs.isPriceUnknown || !actionable,
    label: busy
      ? 'Creating Booking…'
      : inputs.isPriceUnknown
        ? 'Checking price…'
        : inputs.hasOverage
          ? 'Confirm Booking & Pay Overage'
          : 'Confirm Booking',
  };
}
